import React, { useMemo } from "react";
import { CollapsibleSection } from "../components/ui/CollapsibleSection";
import { useLineItemTemplates } from "../hooks/useLineItemTemplates";
import { Rule, RuleTemplateId } from "@rebuild/shared";
import { validateRuleInputs } from "../utils/validateRuleInputs";
import { ValidationError } from "../utils/validationFunctions/types";

type RuleValidationFailure = { rule: Rule; isInOverride: boolean };

interface RulesEngineValidationStatusProps {
  rules: Rule[];
  onRuleSelect: (rule: Rule) => void;
}

interface ValidationCheckBoxProps {
  title: string;
  failures: RuleValidationFailure[];
  onRuleSelect: (rule: Rule) => void;
}

const generateRuleTemplateNameFromId = (templateId: RuleTemplateId): string =>
  templateId
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const ValidationCheckBox: React.FC<ValidationCheckBoxProps> = ({ title, failures, onRuleSelect }) => {
  if (failures.length === 0) return null;

  return (
    <div className="mb-4">
      <p className="text-sm font-medium text-gray-700 mb-2">
        {title} ({failures.length})
      </p>
      <div className="flex flex-wrap gap-2">
        {failures.map(({ rule, isInOverride }) => (
          <button
            key={`${rule.uuid}-${isInOverride ? "override" : "main"}`}
            onClick={() => onRuleSelect(rule)}
            className="text-sm px-3 py-1.5 rounded-lg font-medium cursor-pointer hover:opacity-75 transition-opacity bg-orange-50 border border-orange-300 text-orange-900"
          >
            {[rule.name, isInOverride ? "(override)" : "", rule.isDev ? "(Is Dev)" : ""].filter(Boolean).join(" ")}
          </button>
        ))}
      </div>
    </div>
  );
};

export const RulesEngineValidationStatus: React.FC<RulesEngineValidationStatusProps> = ({ rules, onRuleSelect }) => {
  const { dbLineItems, isLoading } = useLineItemTemplates();

  // Drafts are allowed to be incomplete, so their validation errors aren't reported here.
  const rulesFiltered = useMemo(() => rules.filter((rule) => !rule.isDisabled && !rule.isDraft), [rules]);

  // group all validation errors by rule-template-id -> validation-error-type
  const validationErrorsGroupedByRuleType = useMemo(() => {
    const grouped = new Map<RuleTemplateId, Map<ValidationError, RuleValidationFailure[]>>();

    for (const rule of rulesFiltered) {
      const errors = validateRuleInputs(rule, dbLineItems);
      if (!errors) continue;

      for (const { validationError, isInOverride } of errors.values()) {
        if (!grouped.has(rule.templateId)) {
          grouped.set(rule.templateId, new Map());
        }
        const errorMap = grouped.get(rule.templateId)!;
        if (!errorMap.has(validationError)) {
          errorMap.set(validationError, []);
        }
        errorMap.get(validationError)!.push({ rule, isInOverride: !!isInOverride });
      }
    }

    return grouped;
  }, [rulesFiltered, dbLineItems]);

  // count total number of errors
  const totalErrors = useMemo(
    () =>
      [...validationErrorsGroupedByRuleType.values()].reduce(
        (sum, errorMap) => sum + [...errorMap.values()].reduce((s, failures) => s + failures.length, 0),
        0,
      ),
    [validationErrorsGroupedByRuleType],
  );

  if (isLoading || !rules.length || !dbLineItems?.length) return <></>;

  return (
    <CollapsibleSection title="Rule Validation" badge={totalErrors > 0 ? `${totalErrors} error${totalErrors === 1 ? "" : "s"}` : undefined}>
      {[...validationErrorsGroupedByRuleType.entries()].map(([templateId, errorMap]) => (
        <div key={templateId} className="mb-6">
          <h3 className="text-base font-semibold text-gray-900 mb-3 pb-1 border-b border-gray-200">
            {generateRuleTemplateNameFromId(templateId)}
          </h3>
          {[...errorMap.entries()].map(([validationError, failures]) => (
            <ValidationCheckBox key={validationError} title={validationError} failures={failures} onRuleSelect={onRuleSelect} />
          ))}
        </div>
      ))}
    </CollapsibleSection>
  );
};
